import React, { useEffect, useState } from 'react';

import { Box, Dialog, DialogContent, DialogTitle, Divider, Link, Grid } from '@mui/material';
import {NOTIFICATION_TYPE, showNotificationBar} from '../../../actions/error-pages/NotificationBarActions';
import { getUserId, isAdmin, isUserAdmin } from '../../../actions/profile/UserActions';
import Colors from '../../../constants/Colors';
import { LoadingButton, TextButton } from '../../common/StyledButtons';
import { withRouter } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { updateSiteSubmission } from '../../../actions/internal/SiteActions';
import { formatDateTimeToFancyString } from '../../../utilities/DateUtilities';
import { SUBMISSION_STATUS, SUBMISSION_STATUS_LOOKUP, SUBMISSION_TYPES_HUMAN_MAP } from '../../../constants/Statuses';
import StyledLineItem from '../../common/StyledLineItem';
import { capitalize } from 'lodash';
import ConfirmationModal from './ConfirmationModal';
import EditRequestModal from './EditRequestModal';
import { SUBMISSION_TYPES } from '../../../actions/external/SubmissionActions';
import { formatClockifyTimeInterval } from '../../../utilities/DateUtilities';
import StyledDataTable from '../../common/StyledDataTable';
import StyledDialog from '../../common/StyledDialog';
import { getAllSubmissionsForAdmin, getAllTeamMembersForAdmin } from '../../../actions/internal/AdminActions';
import { LaunchOutlined } from '@mui/icons-material';
import { getAllTeamMembers } from '../../../selectors/AdminSelectors';

const ViewRequestModal = ({ open, onClose, request, match }) => {

  const [ confirmCancelRequest, setConfirmCancelRequest ] = useState(false);
  const [ showEditRequestModal, setShowEditRequestModal ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ isLoadingMembers, setIsLoadingMembers ] = useState(true);

  const teamMembers = getAllTeamMembers();

  useEffect(() => { loadData(); }, [])

  const loadData = async () => {
    try {
      setIsLoadingMembers(true);
      await getAllTeamMembersForAdmin(getUserId());
    } catch (ex) {
      const err = await ex;
      console.error(err);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "Unable to load team member information at this time.");
    } finally {
      setIsLoadingMembers(false);
    }
  }

  const handleClose = () => {
    onClose();
  }

  const handleCancelRequest = async () => {
    try {
      setIsLoading(true)
      setConfirmCancelRequest(false)
      const site_id = request.submission_identifiers.site ? request.submission_identifiers.site.site_id : undefined;
      await updateSiteSubmission(getUserId(), site_id, request.submission_id, ['submission_status'], { submission_status: SUBMISSION_STATUS.CANCELED });
      if (isAdmin()) {
        await getAllSubmissionsForAdmin(getUserId(), true, true);
      }
      showNotificationBar(NOTIFICATION_TYPE.SUCCESS, "This request has been canceled successfully!");
      onClose();
    } catch(ex) {
      console.error(ex);
      showNotificationBar(NOTIFICATION_TYPE.ERROR, "An error has occurred while canceling this request. If this continues to happen, please contact us.");
    } finally {
      setIsLoading(false)
    }
  }

  const getTeamMemberFormattedName = (
    teamMembers,
    user_id,
  ) => {
    if (!teamMembers || !user_id) return undefined;
  
    const mem = teamMembers.find(member => member.user_id === user_id);
    if (!mem) return undefined;

    return `${mem.team_member_details.first_name} ${mem.team_member_details.last_name}`;
  };

  const { submission_received_epoch, submission_type, submission_status, submission_assigned_to, submission_id, submission_fields, submission_attachments, submission_hours, submission_identifiers } = request || {}

  const isClosedSubmission = () => submission_status == SUBMISSION_STATUS.COMPLETED || submission_status == SUBMISSION_STATUS.CANCELED;

  const getFormattedRowsOfHours = (time_entries) => {
    if (!time_entries) return [];
    let hourRows = [];
    let totalDurationSec = 0;
    const rows = time_entries
      .map(entry => {
        const interval = formatClockifyTimeInterval(entry);
        totalDurationSec += interval.duration_in_seconds;
        return {
          id: interval.id,
          "Description": interval.description,
          "Start": interval.time_formatted.start,
          "End": interval.time_formatted.end,
          "Duration": interval.time_formatted.duration
        }
      });
    return { rows, total_duration: totalDurationSec };
  }

  const renderHoursSection = (submission_hours) => {
    const { rows, total_duration } = getFormattedRowsOfHours(submission_hours);
    const duration_in_hours = total_duration / 3600;
    return (
      <>
        <StyledDataTable 
          columns={["Description", "Start", "End", "Duration"]}
          rows={rows}
        />
        <Divider sx={{ borderBottomWidth: 'medium' }} />
        <Grid container justifyContent='flex-start'>
            <Grid item xs={12} md={6}>
                <StyledLineItem primary={"Total Hours"} secondary={(duration_in_hours).toFixed(1)} sx={{ padding: '8px 16px 8px 16px' }} />
            </Grid>
        </Grid>
      </>
    )
  }

  return (
    <>
      <StyledDialog
          fullWidth
          open={open && !confirmCancelRequest && !showEditRequestModal}
          maxWidth={'md'}
      >
          <DialogTitle>
            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
              <Box>
                <h4 style={{ margin: 0, padding: 0 }}>{SUBMISSION_TYPES_HUMAN_MAP[submission_type] || submission_type}</h4>
                <h6 style={{ margin: 0, padding: 0, fontSize: 18, fontWeight: 'normal' }}>Status: <strong>{submission_status ? SUBMISSION_STATUS_LOOKUP[submission_status].label : 'N/A'}</strong></h6>
                {isAdmin() && <h6 style={{ margin: 0, padding: 0, fontSize: 18, fontWeight: 'normal' }}>Assigned To: <strong>{getTeamMemberFormattedName(teamMembers, submission_assigned_to) || 'Unassigned'}</strong></h6>}
                <h6 style={{ margin: 0, padding: 0, fontSize: 18, fontWeight: 'normal' }}><em>Received on {formatDateTimeToFancyString(submission_received_epoch)}</em></h6>
              </Box>
              <Box>
                <h4 style={{ margin: 0, padding: 0, fontSize: 20 }}>{submission_id ? submission_id.substring(submission_id.length - 6).toUpperCase() : 'N/A'}</h4>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
              <h6 style={{ margin: 0, padding: 0, fontSize: 20 }}>Provided Information:</h6>
              {submission_identifiers && submission_identifiers.site &&
              <>
                <StyledLineItem primary={"Account"} secondary={<><Link href={`/admin/sites/${submission_identifiers.site.site_id}/manage`} target="_blank" underline="hover">{submission_identifiers.site.site_name}</Link><LaunchOutlined sx={{ fontSize: '16px', marginBottom: '-4px', marginLeft: '5px'}} /></>} sx={{ padding: '8px 16px 8px 16px' }} />
                <Divider />
              </>
              }
              {submission_fields &&
                <>
                  <StyledLineItem primary={"Title"} secondary={submission_fields["title"]} sx={{ padding: '8px 16px 8px 16px' }} />
                  <Divider />
                  <StyledLineItem primary={"Description"} sx={{ padding: '8px 16px 0px 16px' }} />
                  <div style={{ padding: '8px 16px 8px 16px' }}
                    dangerouslySetInnerHTML={{ __html: submission_fields["description"] }}
                  />
                  <Divider />
                </>
              }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', marginTop: '18px' }}>
              <h6 style={{ margin: 0, padding: 0, fontSize: 20 }}>Attachments:</h6>
              {submission_attachments && submission_attachments.map(attachment => (
                  <Link href={attachment.document_s3_location} target="_blank">{attachment.document_file_name}</Link>
              ))}
              {submission_attachments && submission_attachments.length === 0 &&
                <p>No attachments provided.</p>
              }
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', marginTop: '18px' }}>
              <h6 style={{ margin: 0, padding: 0, fontSize: 20 }}>Recorded Work:</h6>
              {submission_hours && submission_hours.length > 0 &&
                renderHoursSection(submission_hours)
              }
              {!submission_hours || submission_hours.length === 0 &&
                <p>No support hours recorded.</p>
              }
            </Box>
          </DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '16px'}}>
            {!isClosedSubmission() ? <TextButton
              sx={{ marginRight: '10px', color: Colors.redColor }}
              onClick={() => setConfirmCancelRequest(true)}
              disabled={isLoading || (submission_status !== SUBMISSION_STATUS.SUBMITTED && !isUserAdmin())}
            >Cancel Request</TextButton>
            : <div></div>}
            <Box>
              {isAdmin() && (submission_type === SUBMISSION_TYPES.CHANGE_REQUEST || submission_type === SUBMISSION_TYPES.SUPPORT_REQUEST) && !isClosedSubmission() &&
                <TextButton sx={{ marginRight: '10px' }} onClick={() => setShowEditRequestModal(true)} disabled={isLoading || (submission_status !== SUBMISSION_STATUS.SUBMITTED && !isUserAdmin())}>Edit</TextButton>
              }
              <LoadingButton onClick={handleClose} isLoading={isLoading}>Close</LoadingButton>
            </Box>
          </Box>
      </StyledDialog>
      <ConfirmationModal
        open={confirmCancelRequest}
        onCancel={() => setConfirmCancelRequest(false)}
        description={"We are making sure you want to cancel this request. If you proceed, the updates or changes to your website or virtual assets wont be made. Feel free to confirm if you're sure!"}
        okButtonText='Cancel Request'
        cancelButtonText='Go Back'
        onOk={handleCancelRequest}
      />
      <EditRequestModal
        open={showEditRequestModal}
        onClose={() => setShowEditRequestModal(false)}
        onSave={() => {
          setShowEditRequestModal(false);
          handleClose();
        }}
        request={request}
      />
    </>
  )
}

export default observer(withRouter(ViewRequestModal));
// LEFT OFF: NEED TO PROVIDE ABILITY TO CANCEL REQUEST
// NEED TO BE ABLE TO EDIT SUBMITTED DRAFTS!! - Make Modal after submitted status to state that the need to contact us if they want to make a change! Admins can always edit!!
